import { api } from '@/shared/services/api';
import { traceIdFromResponse } from '@/shared/lib/traceId';
import { runStartedAt } from '@/features/admin/lib/logsLink';
import {
  AdminTag,
  AdminUser,
  AdminStats,
  AdminRssFeed,
  CreateRssFeedRequest,
  BackfillDescriptionsResult,
  ProcessRssFeedsResult,
  ServerLogPage,
  ServerLogQuery,
  TagInput,
  TagUpdateInput,
  UpdateRssFeedRequest,
  UserRole,
} from '@/shared/types/api';


/** Drops undefined/null/'' so the server sees only the filters actually set. */
const dropEmpty = <T extends object>(query: T): Partial<T> =>
  Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')) as Partial<T>;

/**
 * Admin panel API. All endpoints require the Admin role server-side; the UI is additionally
 * gated by AdminRoute. Mirrors the per-feature module style of taskApi.ts.
 */
export const adminApi = {
  // Tags (SC-376): the admin-managed tag vocabulary.
  getTags: async (): Promise<AdminTag[]> =>
    (await api.get<AdminTag[]>('/tags', { params: { includeInactive: true } })).data,
  createTag: async (input: TagInput): Promise<AdminTag> => (await api.post<AdminTag>('/tags', input)).data,
  /**
   * A rename is applied server-side to every item carrying the tag. Rejects with a 409 whose body
   * has `reason: 'stale'` when the tag changed after `expectedUpdatedAt` (see isStaleTagEdit).
   */
  updateTag: async (id: number, input: TagUpdateInput): Promise<AdminTag> =>
    (await api.put<AdminTag>(`/tags/${id}`, input)).data,
  /** Soft delete: the tag stops being offered, items that carry it keep it as a legacy tag. */
  deleteTag: async (id: number): Promise<void> => {
    await api.delete(`/tags/${id}`);
  },
  /** Listed tags first, in this order; the server keeps any others after them. */
  reorderTags: async (ids: number[]): Promise<void> => {
    await api.put('/tags/order', { ids });
  },

  // Users
  getUsers: async (): Promise<AdminUser[]> => (await api.get<AdminUser[]>('/admin/users')).data,
  getStats: async (): Promise<AdminStats> => (await api.get<AdminStats>('/admin/stats')).data,
  setAdminRole: async (userId: string, isAdmin: boolean): Promise<void> => {
    await api.put(`/admin/users/${userId}/admin-role`, { isAdmin });
  },
  // SC-238: supersedes setAdminRole, which only expressed two of the three tiers.
  setRole: async (userId: string, role: UserRole): Promise<void> => {
    await api.put(`/admin/users/${userId}/role`, { role });
  },
  activateUser: async (userId: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/activate`);
  },
  deactivateUser: async (userId: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/deactivate`);
  },
  setPassword: async (userId: string, password: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/set-password`, { password });
  },
  deleteUser: async (userId: string): Promise<void> => {
    await api.delete(`/admin/users/${userId}`);
  },

  /**
   * Re-reads the active feeds and replaces stored descriptions with fuller ones (SC-235).
   * Safe to re-run: only a strictly longer description is accepted.
   */
  backfillDescriptions: async (): Promise<BackfillDescriptionsResult> => {
    const startedAt = runStartedAt();
    const res = await api.post<BackfillDescriptionsResult>('/feeditems/backfill-descriptions');
    return { ...res.data, traceId: traceIdFromResponse(res), startedAt };
  },

  /**
   * Runs the ingestion pass that the Hangfire job otherwise runs at 06:00 UTC: fetch every active
   * feed, add new notes, record a version for the ones Fenergo edited, archive the ones that are
   * gone. This — not {@link backfillDescriptions} — is the path that produces versions (SC-278).
   */
  processFeeds: async (): Promise<ProcessRssFeedsResult> => {
    const startedAt = runStartedAt();
    const res = await api.post<ProcessRssFeedsResult>('/feeditems/process');
    return { ...res.data, traceId: traceIdFromResponse(res), startedAt };
  },

  /**
   * Recent server log entries, newest first, read from the API's rolling log files. Cursor-paged:
   * pass the previous page's `nextCursor` as `cursor` to continue further back. Every field is
   * redacted server-side, but it is still untrusted text — render it as text, never as HTML.
   */
  getServerLogs: async (query: ServerLogQuery, signal?: AbortSignal): Promise<ServerLogPage> =>
    (await api.get<ServerLogPage>('/admin/logs', { params: dropEmpty(query), signal })).data,

  // RSS feeds
  getFeeds: async (): Promise<AdminRssFeed[]> => (await api.get<AdminRssFeed[]>('/rssfeeds')).data,
  createFeed: async (request: CreateRssFeedRequest): Promise<void> => {
    await api.post('/rssfeeds', request);
  },
  updateFeed: async (id: number, request: UpdateRssFeedRequest): Promise<void> => {
    await api.put(`/rssfeeds/${id}`, request);
  },
  deleteFeed: async (id: number): Promise<void> => {
    await api.delete(`/rssfeeds/${id}`);
  },
};
